/**
 * jsQR - QR Code decoder library (v1.4.0)
 * Bundled locally for offline-first capability.
 *
 * Original: https://github.com/cozmo/jsQR
 * License: Apache-2.0
 *
 * Usage:
 *   import jsQR from '../lib/jsQR.js';
 *   const result = jsQR(imageData.data, width, height);
 */

// The UMD bundle doesn't provide an ESM default export.
// Load it as a script tag (puts jsQR on window) and re-export.
const script = document.createElement('script');
script.src = new URL('./jsQR.umd.js', import.meta.url).href;
document.head.appendChild(script);

// Synchronous fallback — the script is loaded synchronously from cache in dev,
// but we also provide an init function for guaranteed availability.
function getJsQR() {
  return window.jsQR;
}

/**
 * Ensure jsQR is loaded (call before first use)
 * @returns {Promise<Function>}
 */
export async function initJsQR() {
  if (window.jsQR) return window.jsQR;
  return new Promise((resolve, reject) => {
    script.onload = () => resolve(window.jsQR);
    script.onerror = () => reject(new Error('Failed to load jsQR'));
    // If already loaded by the time we attach
    if (window.jsQR) resolve(window.jsQR);
  });
}

// Default export — a thin wrapper that delegates to window.jsQR
// By the time QrScanner._tick() runs, the script will have loaded
// because QrScanner only starts after user interaction (camera permission)
export default function jsQR(data, width, height, options) {
  const fn = getJsQR();
  if (!fn) {
    console.warn('jsQR not yet loaded');
    return null;
  }
  return fn(data, width, height, options);
}
